import React from 'react';
import { Box, Grid2 as Grid, styled, useMediaQuery, useTheme } from '@mui/material';
import HighlighterHeader, { HighlighterBackground } from './HighlighterHeader';
import PaymentSummary from './PaymentSummary';
import { PlanSummary } from '../types';
import CardWithBoxShadow from '../../common/components/CardWithBoxShadow';
import Loading from '../../common/components/Loading';
import TertiaryButton from '../../common/components/TertiaryButton';
import PrimaryButton from '../../common/components/PrimaryButton';

export interface Props {
    maxAthleteCount: number;
    finalPlanSummary: PlanSummary;
    handleBack: () => void;
    isLoading: boolean;
    isPaymentProcessing: boolean;
    handlePurchase: () => Promise<void>;
}

const EndDivider = styled(Box)({
    height: '0px',
    border: 1,
    borderColor: '#EEE',
    borderStyle: 'solid',
    flexShrink: 0,
    marginBottom: 32,
    marginTop: 16,
    marginRight: 8,
    marginLeft: 8
});

export default function ReviewPurchasePage({
    maxAthleteCount,
    finalPlanSummary,
    handleBack,
    isLoading,
    isPaymentProcessing,
    handlePurchase
}: Props) {
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    if (isLoading) {
        return <Loading />;
    }

    return (
        <Box marginX={4}>
            <HighlighterHeader
                title="Review Purchase"
                highlighterBackground={HighlighterBackground.REVIEW_PURCHASE}
                width={isSmallScreen ? 265 : 307}
            />
            <Box display="flex" flex={1} justifyContent="center">
                <Grid display="flex" flex={1} container sx={{ maxWidth: 600 }}>
                    <Grid display="block" flex={1} container>
                        <CardWithBoxShadow>
                            <Box paddingY={isSmallScreen ? 3 : 4} paddingX={isSmallScreen ? 2 : 9}>
                                <PaymentSummary
                                    maxAthleteCount={maxAthleteCount}
                                    planSummary={finalPlanSummary}
                                    showHeader={false}
                                />
                                {isMediumScreen && <EndDivider />}
                                <Grid
                                    container
                                    display="flex"
                                    flex={1}
                                    spacing={2}
                                    sx={{
                                        flexDirection: isMediumScreen ? 'column-reverse' : null,
                                        paddingTop: isMediumScreen ? null : '42px'
                                    }}
                                >
                                    <Grid size={{ xs: 12, md: 5 }}>
                                        <TertiaryButton
                                            data-testid="review-purchase-back-button"
                                            onClick={handleBack}
                                            sx={{ width: isMediumScreen ? '100%' : null }}
                                        >
                                            Back
                                        </TertiaryButton>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 7 }}>
                                        <PrimaryButton
                                            data-testid="review-purchase-purchase-button"
                                            sx={{ width: '100%' }}
                                            onClick={handlePurchase}
                                            loading={isPaymentProcessing}
                                        >
                                            Purchase
                                        </PrimaryButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardWithBoxShadow>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
